/**
 * WidgetRenderer Component
 *
 * Dispatcher component that routes widgets to their appropriate
 * renderer based on widgetType. Handles config merging with
 * shared widgets and default configurations.
 */

import React from 'react';
import { WidgetRendererProps } from './types';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import {
  BUTTON_DEFAULT_CONFIG,
  DIVIDER_DEFAULT_CONFIG,
  YOUR_BUILD_DEFAULT_CONFIG,
  ButtonWidgetConfig,
  DividerWidgetConfig,
  YourBuildWidgetConfig,
} from '../../config/widgetDefaults';
import DividerWidget from './DividerWidget';
import ButtonWidget from './ButtonWidget';
import ContentBlockWidget from './ContentBlockWidget';
import YourBuildWidget from './YourBuildWidget';

/**
 * Get configuration by merging: shared → embedded → default
 */
function mergeConfig<T>(
  defaultConfig: T,
  sharedConfig?: Record<string, unknown>,
  embeddedConfig?: Record<string, unknown>
): T {
  return {
    ...defaultConfig,
    ...sharedConfig,
    ...embeddedConfig,
  } as T;
}

export const WidgetRenderer = ({
  widget,
  isSelected,
  onSelect,
  onContentChange,
}: WidgetRendererProps) => {
  const { state } = useUIModelState();

  // Get shared widget configuration from model-level sharedWidgets
  const getSharedConfig = (widgetKey: string): Record<string, unknown> | undefined => {
    return state.model?.layout?.sharedWidgets?.[widgetKey] as Record<string, unknown> | undefined;
  };

  // Get embedded configuration from widget
  const embeddedConfig = widget.configuration as Record<string, unknown> | undefined;

  // Render based on widget type
  switch (widget.widgetType) {
    case 'divider': {
      const config = mergeConfig<DividerWidgetConfig>(
        DIVIDER_DEFAULT_CONFIG,
        undefined,
        embeddedConfig
      );
      return (
        <DividerWidget
          config={config}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );
    }

    case 'button': {
      const config = mergeConfig<ButtonWidgetConfig>(
        BUTTON_DEFAULT_CONFIG,
        undefined,
        embeddedConfig
      );
      return (
        <ButtonWidget
          config={config}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );
    }

    case 'contentBlock': {
      return (
        <ContentBlockWidget
          widget={widget}
          isSelected={isSelected}
          onSelect={onSelect}
          onContentChange={
            onContentChange
              ? (content) => onContentChange(widget.id, content)
              : undefined
          }
        />
      );
    }

    case 'yourBuild': {
      // YourBuild uses shared config from model-level
      // Shared config is the source of truth; embedded config is legacy fallback only
      const sharedConfig = getSharedConfig('yourBuildWidget');
      const config = {
        ...YOUR_BUILD_DEFAULT_CONFIG,
        ...(embeddedConfig || {}),
        ...(sharedConfig || {}),
      } as YourBuildWidgetConfig;
      return (
        <YourBuildWidget
          config={config}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );
    }

    case 'build': {
      // Build widget - simplified summary view
      return (
        <div role="button" data-testid="widgets-widgetrenderer-div-1"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className={`tw-w-full tw-p-4 tw-border tw-border-edge tw-rounded tw-bg-surface-sunken tw-cursor-pointer tw-transition-all ${
            isSelected ? 'tw-ring-2 tw-ring-accent tw-ring-offset-2' : ''
          }`}
        >
          <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
            <span className="tw-text-sm tw-font-medium tw-text-ink">
              Build Widget
            </span>
          </div>
          <p className="tw-text-xs tw-text-ink-secondary tw-italic">
            Build summary component (runtime only)
          </p>
        </div>
      );
    }

    default: {
      // Unknown widget type - render placeholder
      return (
        <div role="button" data-testid="widgets-widgetrenderer-div-2"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className={`tw-w-full tw-p-3 tw-bg-surface-sunken tw-border tw-border-edge tw-rounded tw-cursor-pointer tw-transition-all ${
            isSelected ? 'tw-ring-2 tw-ring-accent tw-ring-offset-2' : ''
          }`}
        >
          <p className="tw-text-sm tw-text-ink-secondary">
            Unknown widget: <code className="tw-bg-surface-hover tw-px-1 tw-rounded">{widget.widgetType}</code>
          </p>
          <p className="tw-text-xs tw-text-ink-tertiary tw-mt-1">
            Widget ID: {widget.id}
          </p>
        </div>
      );
    }
  }
};

export default WidgetRenderer;
